/**
 * Product page interactivity: variant selection -> price/media/availability sync.
 * No framework — three small custom elements, plumbed together with events.
 *
 * Price is re-fetched via the Section Rendering API (?section_id=) instead of
 * computed client-side, so Shopify Markets currency conversion and money
 * formatting stay correct without duplicating that logic in JS.
 */

class VariantPicker extends HTMLElement {
  constructor() {
    super();
    this.variants = JSON.parse(this.querySelector('[data-variant-data]').textContent);
    this.colorMediaMap = JSON.parse(this.querySelector('[data-color-media-map]').textContent);
    this.addEventListener('change', this.onChange.bind(this));
  }

  onChange(event) {
    const input = event.target;
    if (!input.classList.contains('product-option__input')) return;

    const fieldset = input.closest('.product-option');
    const selectedValueEl = fieldset.querySelector('[data-selected-value]');
    if (selectedValueEl) selectedValueEl.textContent = input.value;

    const selectedOptions = this.getSelectedOptions();
    const variant = this.findVariant(selectedOptions);

    this.dispatchEvent(
      new CustomEvent('variant:change', {
        bubbles: true,
        detail: {
          variant,
          optionName: fieldset.dataset.optionName,
          optionValue: input.value,
        },
      })
    );
  }

  getSelectedOptions() {
    return [...this.querySelectorAll('.product-option')]
      .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
      .map((fieldset) => {
        const checked = fieldset.querySelector('.product-option__input:checked');
        if (checked) return checked.value;

        // No radio marked :checked in this group (shouldn't normally happen —
        // the server always pre-selects a value) — fall back to the value
        // already shown in the legend rather than reporting this option as
        // unset, which would make every variant lookup fail and the button
        // wrongly flip to "unavailable".
        const displayedValue = fieldset.querySelector('[data-selected-value]');
        return displayedValue ? displayedValue.textContent.trim() : null;
      });
  }

  findVariant(selectedOptions) {
    return this.variants.find((variant) =>
      selectedOptions.every((value, index) => variant.options[index] === value)
    );
  }
}

class ProductMediaGallery extends HTMLElement {
  constructor() {
    super();
    this.stage = this.querySelector('[data-gallery-stage]');
  }

  /**
   * Shows the media belonging to one color and hides the rest. Display order
   * comes from the DOM (product-media-gallery.liquid renders each color's media
   * grouped and ordered flat -> male -> female), so this only toggles.
   */
  setActiveMediaGroup(mediaIds) {
    if (!this.stage || !Array.isArray(mediaIds) || !mediaIds.length) return;

    const wanted = new Set(mediaIds.map(String));
    this.stage.querySelectorAll('.product-gallery__item').forEach((el) => {
      const isVisible = wanted.has(el.dataset.mediaId);
      el.classList.toggle('is-active', isVisible);
      el.hidden = !isVisible;
    });

    // Drives --gallery-columns so one or two images do not stretch across three.
    this.stage.dataset.visibleCount = String(wanted.size);
    // Mobile is a horizontal scroller — start the new group at its first image.
    this.stage.scrollTo({ left: 0, behavior: 'auto' });
  }
}

class ProductFormComponent extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('[data-product-form]') || this.querySelector('form');
    this.picker = this.querySelector('variant-picker');
    this.priceSlot = this.querySelector('[data-price-slot]');
    this.submitButton = this.querySelector('[data-add-to-cart]');
    this.submitButtonText = this.submitButton
      ? this.submitButton.querySelector('[data-add-to-cart-text]')
      : null;
    this.variantIdInput = this.querySelector('[data-variant-id-input]');
    this.sectionId = this.picker ? this.picker.dataset.sectionId : null;
    this.productUrl = this.dataset.productUrl;

    this.addEventListener('variant:change', this.onVariantChange.bind(this));
    if (this.form) this.form.addEventListener('submit', this.onSubmit.bind(this));
  }

  onVariantChange(event) {
    const { variant, optionName, optionValue } = event.detail;

    // The gallery follows the color swatch, not the resolved variant: colors the
    // catalog does not stock in the currently selected size (Pepper has no S on
    // the Graphic Tee, for one) still have to show their own photos, so this runs
    // before the no-variant bail-out below.
    if (optionName === 'Color' || optionName === 'Colour') {
      const gallery = this.closest('.product')?.querySelector('product-media-gallery');
      const mediaIds = this.picker.colorMediaMap[optionValue];
      if (gallery && mediaIds) gallery.setActiveMediaGroup(mediaIds);
    }

    if (!variant) {
      this.setUnavailable();
      return;
    }

    if (this.variantIdInput) this.variantIdInput.value = variant.id;
    if (this.productUrl && window.history.replaceState) {
      window.history.replaceState({}, '', `${this.productUrl}?variant=${variant.id}`);
    }

    this.updatePrice(variant);
  }

  setUnavailable() {
    if (this.submitButton) this.submitButton.disabled = true;
    if (this.submitButtonText) {
      this.submitButtonText.textContent = window.themeStrings?.unavailable || 'Unavailable';
    }
  }

  async updatePrice(variant) {
    if (!this.sectionId || !this.productUrl) return;

    try {
      const url = `${this.productUrl}?variant=${variant.id}&section_id=${this.sectionId}`;
      const response = await fetch(url);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const freshPrice = doc.querySelector('[data-price-slot]');

      if (freshPrice && this.priceSlot) this.priceSlot.innerHTML = freshPrice.innerHTML;
    } catch (error) {
      // Non-fatal: price slot just keeps its last known value.
    }

    if (this.submitButton) this.submitButton.disabled = !variant.available;
    if (this.submitButtonText) {
      this.submitButtonText.textContent = variant.available
        ? window.themeStrings?.addToCart || 'Add to cart'
        : window.themeStrings?.soldOut || 'Sold out';
    }
  }

  async onSubmit(event) {
    event.preventDefault();
    if (!this.form || !this.submitButton) return;

    const formData = new FormData(this.form);
    this.submitButton.disabled = true;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });
      const data = await response.json();

      if (!response.ok) throw new Error(data.description || 'Add to cart failed');

      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { item: data } }));
      document.dispatchEvent(new CustomEvent('cart:open'));
    } catch (error) {
      document.dispatchEvent(
        new CustomEvent('cart:error', { detail: { message: error.message } })
      );
    } finally {
      this.submitButton.disabled = false;
    }
  }
}

customElements.define('variant-picker', VariantPicker);
customElements.define('product-media-gallery', ProductMediaGallery);
customElements.define('product-form-component', ProductFormComponent);
